import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import KpiCard from '../components/KpiCard';
import { NFModal } from '../components/NFModal';
import { PedidoConferenciaModal } from '../components/PedidoConferenciaModal';
import SortableTh from '../components/SortableTh';
import TablePagination from '../components/TablePagination';
import DateRangePicker from '../components/DateRangePicker';
import { fmtDate, fmtMoney, fmtMoney2 } from '../lib/fmt';
import { nfSeverity } from '../lib/nfStatus';
import { compareValues, paginateRows } from '../lib/table';
import type { NFOut, PaginatedResponse, PedidoOut } from '../types/api';

type PedidoRow = PedidoOut & {
  nfs: NFOut[];
  valor_nfs: number;
  pct_faturado: number;
  data_emissao_nf: string | null;
  data_competencia_nf: string | null;
  status_nf: 'aguardando' | 'pendente' | 'divergente' | 'conferido';
};

function currentMonthRange() {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(now) };
}

const APROVADO_OU_PAGO = new Set(['Aprovado', 'Pago parcial', 'Pago total']);

function normalizeDigits(value: string | null | undefined) {
  return String(value ?? '').replace(/\D/g, '');
}

export default function ConferenciaNF() {
  const [periodRange, setPeriodRange] = useState(currentMonthRange);
  const [sortBy, setSortBy] = useState<'numero' | 'fornecedor' | 'valor_total' | 'valor_nfs' | 'pct_faturado' | 'data_emissao_nf' | 'data_competencia_nf' | 'created_at'>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [pedidoSearch, setPedidoSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'todos' | PedidoRow['status_nf']>('todos');
  const [showModal, setShowModal] = useState(false);
  const [modalPedidoId, setModalPedidoId] = useState<number | null>(null);
  const [editingNF, setEditingNF] = useState<NFOut | null>(null);
  const [pedidoModalId, setPedidoModalId] = useState<number | null>(null);
  const [hubInitialNF, setHubInitialNF] = useState<NFOut | 'new' | null>(null);
  const [bonifExpandida, setBonifExpandida] = useState<string | null>(null);
  const pageSize = 15;

  const { from: de, to: ate } = periodRange;

  // O período é filtrado no client por competência (fallback emissão), então buscamos amplo.
  const pedParams = new URLSearchParams({ limit: '1000' });
  const nfParams = new URLSearchParams({ limit: '1000' });

  const { data: pedidosPage, isLoading: loadingPedidos } = useQuery<PaginatedResponse<PedidoOut>>({
    queryKey: ['pedidos-conf-nf'],
    queryFn: () => api.get<PaginatedResponse<PedidoOut>>(`/pedidos?${pedParams}`).then(r => r.data),
  });

  const { data: nfsPage, isLoading: loadingNfs } = useQuery<PaginatedResponse<NFOut>>({
    queryKey: ['nfs', 'all'],
    queryFn: () => api.get<PaginatedResponse<NFOut>>(`/nfs?${nfParams}`).then(r => r.data),
  });

  const pedidos = (pedidosPage?.items ?? []).filter(pedido => APROVADO_OU_PAGO.has(pedido.status_po));
  const nfs = nfsPage?.items ?? [];

  const bonificacoes = useMemo(() => nfs.filter(nf => nf.tipo_nf === 'bonificacao'), [nfs]);
  const nfsComuns = useMemo(() => nfs.filter(nf => nf.tipo_nf === 'comum'), [nfs]);

  const pedidoRows = useMemo<PedidoRow[]>(() => {
    const byPedido = new Map<number, NFOut[]>();
    nfsComuns.forEach(nf => {
      if (!nf.pedido_id) return;
      const list = byPedido.get(nf.pedido_id) ?? [];
      list.push(nf);
      byPedido.set(nf.pedido_id, list);
    });

    return pedidos.map(pedido => {
      const linked = byPedido.get(pedido.id) ?? [];
      const valorNfs = linked.reduce((sum, nf) => sum + Number(nf.valor_total), 0);
      const pct = Number(pedido.valor_total) > 0 ? (valorNfs / Number(pedido.valor_total)) * 100 : 0;
      const latestEmission = linked.reduce<string | null>(
        (latest, nf) => (!latest || nf.data_emissao > latest ? nf.data_emissao : latest),
        null,
      );
      const latestCompetencia = linked.reduce<string | null>(
        (latest, nf) => {
          const comp = nf.data_competencia ?? nf.data_emissao;
          return !latest || comp > latest ? comp : latest;
        },
        null,
      );
      let status: PedidoRow['status_nf'] = 'aguardando';
      if (linked.length > 0) {
        const statuses = linked.map(nfSeverity);
        const anyDivergente = statuses.some(item => item === 'divergente');
        // Pedido faturado em várias NFs: quando o somatório fecha o valor do pedido
        // (≈100%), a "NF parcial" individual deixa de rebaixar o status do pedido.
        const somaFecha = linked.length > 1 && pct >= 99 && pct <= 101;
        status = anyDivergente
          ? 'divergente'
          : somaFecha
            ? 'conferido'
            : statuses.some(item => item === 'pendente')
              ? 'pendente'
              : 'conferido';
      }
      return {
        ...pedido,
        nfs: linked,
        valor_nfs: valorNfs,
        pct_faturado: pct,
        data_emissao_nf: latestEmission,
        data_competencia_nf: latestCompetencia,
        status_nf: status,
      };
    });
  }, [nfsComuns, pedidos]);

  const rowsFiltered = useMemo(() => {
    const search = pedidoSearch.trim().toLowerCase();
    return pedidoRows.filter(row => {
      const matchesPedido = !search || row.numero.toLowerCase().includes(search);
      const matchesStatus = statusFilter === 'todos' || row.status_nf === statusFilter;
      // Filtro de período por competência (fallback emissão); pedidos sem NF usam a data do pedido.
      const refDate = (row.data_competencia_nf ?? row.created_at)?.slice(0, 10);
      const matchesPeriodo = (!de || (refDate != null && refDate >= de)) && (!ate || (refDate != null && refDate <= ate));
      return matchesPedido && matchesStatus && matchesPeriodo;
    });
  }, [pedidoRows, pedidoSearch, statusFilter, de, ate]);

  const rowsSorted = useMemo(
    () => [...rowsFiltered].sort((a, b) => compareValues(a[sortBy], b[sortBy], sortDir)),
    [rowsFiltered, sortBy, sortDir],
  );
  const rowsPaged = paginateRows(rowsSorted, page, pageSize);
  const pedidoModalRow = pedidoModalId != null ? pedidoRows.find(row => row.id === pedidoModalId) ?? null : null;

  const totalBonif = bonificacoes.reduce((sum, nf) => sum + Number(nf.valor_total), 0);
  const totalComDiverg = nfsComuns.filter(nf => ['divergente', 'pendente'].includes(nfSeverity(nf))).length;
  const totalConferidas = nfsComuns.filter(nf => nfSeverity(nf) === 'conferido').length;
  const pedidosSemNf = pedidoRows.filter(row => row.nfs.length === 0).length;

  const bonifByFornecedor = useMemo(() => {
    const map = new Map<string, { key: string; fornecedor: string; total: number; qtd: number; nfs: NFOut[] }>();
    bonificacoes.forEach(nf => {
      const key = normalizeDigits(nf.cnpj_emitente) || nf.emitente.toLowerCase();
      const current = map.get(key) ?? { key, fornecedor: nf.emitente, total: 0, qtd: 0, nfs: [] };
      current.total += Number(nf.valor_total);
      current.qtd += 1;
      current.nfs.push(nf);
      map.set(key, current);
    });
    return [...map.values()].sort((a, b) => b.total - a.total).slice(0, 8);
  }, [bonificacoes]);

  function onSort(field: typeof sortBy) {
    if (sortBy === field) setSortDir(current => current === 'desc' ? 'asc' : 'desc');
    else {
      setSortBy(field);
      setSortDir(field === 'numero' || field === 'fornecedor' ? 'asc' : 'desc');
    }
    setPage(1);
  }

  const isLoading = loadingPedidos || loadingNfs;

  return (
    <div className="space-y-6 fade-in">
      <div className="card p-5 glow" style={{ background: 'linear-gradient(135deg,#16233B 0%,#0E1A2E 100%)', borderLeft: '4px solid #F47A20' }}>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="text-4xl">📑</div>
          <div className="flex-1 min-w-[200px]">
            <h3 className="text-white font-extrabold text-lg">Importar Nota Fiscal de Produto</h3>
            <p className="text-xs text-mbc-mute mt-0.5">Anexe o PDF da NF, associe ao pedido aprovado ou pago e confira automaticamente fornecedor, valor e condição de pagamento.</p>
          </div>
          <button
            type="button"
            onClick={() => { setEditingNF(null); setModalPedidoId(null); setShowModal(true); }}
            className="bg-mbc-orange hover:bg-mbc-orangel text-white font-bold px-6 py-3 rounded-xl shadow-lg pill-btn text-sm"
          >
            📑 Anexar nova NF
          </button>
        </div>
      </div>

      <DateRangePicker
        from={de}
        to={ate}
        onChange={(range) => { setPeriodRange(range); setPage(1); }}
        onClear={() => setPage(1)}
        className="max-w-[360px]"
      />

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard label="NFs anexadas" value={String(nfsComuns.length)} sub={`${totalConferidas} conferidas`} accent={nfsComuns.length ? 'mbc-orangel' : 'mbc-mute'} icon="📑" />
        <KpiCard label="Conferidas" value={String(totalConferidas)} sub="pedido bate com a NF" accent="emerald-300" icon="✓" />
        <KpiCard label="Pendentes / Diverg." value={String(pedidosSemNf + totalComDiverg)} sub="pedidos aguardando ação" accent={totalComDiverg > 0 ? 'rose-300' : 'amber-300'} icon="⏳" />
        <KpiCard label="Bonificações" value={fmtMoney(totalBonif)} sub={`${bonificacoes.length} NF(s)`} accent={bonificacoes.length ? 'emerald-300' : 'mbc-mute'} icon="🎁" />
      </div>

      <div className="card p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="text-white font-bold flex items-center gap-2">🎁 Bonificações por fornecedor</h3>
            <p className="text-xs text-mbc-mute mt-0.5">Notas marcadas como bonificação ficam agregadas fora da conferência por pedido.</p>
          </div>
        </div>
        {bonifByFornecedor.length === 0 ? (
          <div className="text-center text-mbc-mute text-sm py-8">Nenhuma bonificação no período.</div>
        ) : (
          <div className="scroller">
            <table className="dt">
              <thead>
                <tr>
                  <th>Fornecedor</th>
                  <th className="num">NFs</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {bonifByFornecedor.map(row => {
                  const expanded = bonifExpandida === row.key;
                  return (
                    <Fragment key={row.key}>
                      <tr
                        className="cursor-pointer hover:bg-white/5"
                        onClick={() => setBonifExpandida(current => current === row.key ? null : row.key)}
                        title="Clique para ver as NFs individuais"
                      >
                        <td>
                          <span className="mr-1.5 text-mbc-mute">{expanded ? '▾' : '▸'}</span>
                          {row.fornecedor}
                        </td>
                        <td className="num">{row.qtd}</td>
                        <td className="num text-emerald-300 font-semibold">{fmtMoney2(row.total)}</td>
                      </tr>
                      {expanded && (
                        <tr>
                          <td colSpan={3} className="bg-black/20">
                            <div className="flex flex-wrap gap-2 py-1">
                              {row.nfs.map(nf => (
                                <button
                                  key={nf.id}
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setEditingNF(nf); setModalPedidoId(null); setShowModal(true); }}
                                  className="flex items-center gap-2 rounded-lg border border-mbc-line bg-mbc-panel2 px-2.5 py-1.5 text-xs text-mbc-soft hover:border-mbc-orange hover:text-white"
                                  title="Abrir para editar ou excluir esta bonificação"
                                >
                                  <span className="font-mono text-mbc-bluel">NF {nf.numero}</span>
                                  <span className="text-mbc-mute">{fmtDate(nf.data_emissao)}</span>
                                  <span className="text-emerald-300 font-semibold">{fmtMoney2(Number(nf.valor_total))}</span>
                                </button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-white font-bold flex items-center gap-2">📋 Pedidos aprovados e NFs anexadas</h3>
            <p className="text-xs text-mbc-mute mt-0.5">Clique no pedido ou em uma NF para abrir o popup de importação e conferência.</p>
          </div>
        </div>
        <div className="mb-4 flex flex-wrap gap-3">
          <input
            type="text"
            value={pedidoSearch}
            onChange={(e) => { setPedidoSearch(e.target.value); setPage(1); }}
            placeholder="Buscar por número do pedido…"
            className="min-w-[220px] flex-1 rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-2 text-sm text-white placeholder:text-mbc-mute focus:border-mbc-orange focus:outline-none"
          />
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as typeof statusFilter); setPage(1); }}
            className="min-w-[200px] rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-2 text-sm text-white focus:border-mbc-orange focus:outline-none"
          >
            <option value="todos">Todos os status</option>
            <option value="aguardando">Aguardando NF</option>
            <option value="pendente">Pendente</option>
            <option value="divergente">Divergente</option>
            <option value="conferido">Conferido</option>
          </select>
        </div>
        <div className="scroller">
          {isLoading ? (
            <div className="p-8 text-center text-mbc-mute">Carregando…</div>
          ) : rowsFiltered.length === 0 ? (
            <div className="p-8 text-center text-mbc-mute">Nenhum pedido aprovado ou pago no período.</div>
          ) : (
            <table className="dt">
              <thead>
                <tr>
                  <SortableTh label="Pedido" active={sortBy === 'numero'} direction={sortDir} onClick={() => onSort('numero')} style={{ width: 110 }} />
                  <SortableTh label="Data de emissão" active={sortBy === 'data_emissao_nf'} direction={sortDir} onClick={() => onSort('data_emissao_nf')} style={{ width: 130 }} />
                  <SortableTh label="Competência" active={sortBy === 'data_competencia_nf'} direction={sortDir} onClick={() => onSort('data_competencia_nf')} style={{ width: 120 }} />
                  <SortableTh label="Fornecedor" active={sortBy === 'fornecedor'} direction={sortDir} onClick={() => onSort('fornecedor')} className="text-left" style={{ width: 220 }} />
                  <SortableTh label="Valor pedido" active={sortBy === 'valor_total'} direction={sortDir} onClick={() => onSort('valor_total')} className="num" style={{ width: 130 }} />
                  <th className="text-center" style={{ width: 80 }}>NFs</th>
                  <SortableTh label="Total NFs" active={sortBy === 'valor_nfs'} direction={sortDir} onClick={() => onSort('valor_nfs')} className="num" style={{ width: 130 }} />
                  <SortableTh label="% Faturado" active={sortBy === 'pct_faturado'} direction={sortDir} onClick={() => onSort('pct_faturado')} className="num" style={{ width: 110 }} />
                  <th className="text-center" style={{ width: 160 }}>Conferência</th>
                  <th className="text-center" style={{ width: 260 }}>NFs</th>
                </tr>
              </thead>
              <tbody>
                {rowsPaged.map(row => (
                  <tr key={row.id}>
                    <td>
                      <button
                        type="button"
                        onClick={() => { setHubInitialNF(null); setPedidoModalId(row.id); }}
                        className="font-mono text-mbc-bluel text-xs hover:text-mbc-orangel hover:underline"
                        title="Abrir conferência do pedido"
                      >
                        {row.numero}
                      </button>
                    </td>
                    <td>{row.data_emissao_nf ? fmtDate(row.data_emissao_nf) : '—'}</td>
                    <td className={row.data_competencia_nf ? 'text-mbc-bluel' : ''}>{row.data_competencia_nf ? fmtDate(row.data_competencia_nf) : '—'}</td>
                    <td><span className="truncate inline-block max-w-[190px]" title={row.fornecedor}>{row.fornecedor}</span></td>
                    <td className="num font-semibold">{fmtMoney2(Number(row.valor_total))}</td>
                    <td className="text-center">{row.nfs.length || '—'}</td>
                    <td className="num">{row.valor_nfs ? fmtMoney2(row.valor_nfs) : '—'}</td>
                    <td className="num">
                      <span className={row.pct_faturado >= 100 ? 'text-emerald-300' : row.pct_faturado >= 50 ? 'text-mbc-bluel' : 'text-amber-300'}>
                        {row.pct_faturado ? `${row.pct_faturado.toFixed(1)}%` : '0%'}
                      </span>
                    </td>
                    <td className="text-center">
                      {row.status_nf === 'aguardando' ? (
                        <span className="badge bg-amber-500/15 text-amber-300">⏳ aguardando NF</span>
                      ) : row.status_nf === 'pendente' ? (
                        <span className="badge bg-mbc-mute/30 text-mbc-soft">⏳ falta boleto real</span>
                      ) : row.status_nf === 'divergente' ? (
                        <span className="badge bg-rose-500/25 text-rose-300">⚠ divergente</span>
                      ) : (
                        <span className="badge bg-emerald-500/25 text-emerald-300">✓ conferido</span>
                      )}
                    </td>
                    <td className="text-center">
                      <div className="flex gap-1 justify-center flex-wrap">
                        {row.nfs.map(nf => {
                          const sev = nfSeverity(nf);
                          const cls = sev === 'conferido'
                            ? 'bg-emerald-500/30 text-emerald-200'
                            : sev === 'divergente'
                              ? 'bg-rose-500/25 text-rose-300'
                              : 'bg-mbc-mute/20 text-mbc-soft';
                          return (
                            <button key={nf.id} type="button" onClick={() => { setHubInitialNF(nf); setPedidoModalId(row.id); }} className={`text-[10px] px-1.5 py-0.5 rounded ${cls}`} title={`${nf.emitente} · ${fmtDate(nf.data_emissao)}`}>
                              NF {nf.numero}
                            </button>
                          );
                        })}
                        <button type="button" onClick={() => { setHubInitialNF('new'); setPedidoModalId(row.id); }} className="text-xs bg-mbc-orange/20 hover:bg-mbc-orange/40 text-mbc-orangel px-2 py-1 rounded" title="Anexar nova NF">
                          ＋
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="mt-4">
          <TablePagination total={rowsFiltered.length} page={page} pageSize={pageSize} onPageChange={setPage} />
        </div>
      </div>

      {pedidoModalRow && (
        <PedidoConferenciaModal
          pedido={pedidoModalRow}
          nfs={pedidoModalRow.nfs}
          initialNF={hubInitialNF}
          onClose={() => { setPedidoModalId(null); setHubInitialNF(null); }}
        />
      )}

      {showModal && (
        <NFModal
          pedidos={pedidos}
          existingNF={editingNF}
          suggestedPedidoId={modalPedidoId}
          onClose={() => {
            setEditingNF(null);
            setModalPedidoId(null);
            setShowModal(false);
          }}
        />
      )}
    </div>
  );
}
